import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { serverTypeLabel, CREATABLE_SERVER_TYPES } from '@/utils/server-type';
import WvdCard from '@/components/dazn/WvdCard';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import {
  daznService,
  proxyService,
  xaccelService,
  accountService,
  xaccelProxyService,
  getErrorMessage,
} from '@/services';
import type { Proxy, XaccelServer } from '@/services';
import {
  DAZN_COUNTRIES,
  TIMEZONES,
  COUNTRY_TIMEZONE_MAP,
  COUNTRY_LANGUAGE_MAP,
  DAZN_DEFAULTS,
} from '@/utils/dazn-constants';
import { wizardIdentityFields, identityPayload } from '@/utils/dazn-account-form';
import { Zap, Check, ChevronRight, ChevronLeft, Server, Shield, User, Loader2, LogOut, Download, AlertCircle, Globe } from 'lucide-react';

// --- Schemas ---
const daznStepSchema = z.object({
  dazn_email: z.string().email('validation.invalidEmail'),
  dazn_password: z.string().optional(),
  dazn_device_id: z.string().optional(),
  dazn_country: z.string().min(2, 'validation.required'),
  dazn_language: z.string().min(2, 'validation.required'),
  dazn_timezone: z.string().min(1, 'validation.required'),
  dazn_pin: z.string().max(4).optional(),
  dazn_user_agent: z.string().optional(),
  dazn_x_dazn_ua: z.string().optional(),
});

const proxyStepSchema = z.object({
  name: z.string().optional(),
  proxy_host: z.string().min(1, 'validation.required'),
  proxy_port: z.coerce.number().min(1).max(65535),
  proxy_type: z.enum(['http', 'https', 'socks5']),
  proxy_username: z.string().optional(),
  proxy_password: z.string().optional(),
});

const xaccelStepSchema = z.object({
  name: z.string().optional(),
  server_url: z.string().url('validation.invalidUrl'),
  server_token: z.string().min(1, 'validation.required'),
  server_type: z.enum(['xaccel', 'ssp']).default('xaccel'),
});

type DaznStepForm = z.infer<typeof daznStepSchema>;
type ProxyStepForm = z.infer<typeof proxyStepSchema>;
type XaccelStepForm = z.infer<typeof xaccelStepSchema>;

// --- Step Indicator ---
// Order: Proxy (1) -> DAZN (2) -> Xaccel (3) -> Summary (4)
function StepIndicator({ currentStep }: { currentStep: number }) {
  const { t } = useTranslation();
  const steps = [
    { num: 1, label: t('wizard.step2') }, // Proxy
    { num: 2, label: t('wizard.step1') }, // Account (DAZN)
    { num: 3, label: t('wizard.step3') }, // Xaccel
    { num: 4, label: t('wizard.step4') }, // Summary
  ];

  return (
    <div className="flex items-center justify-center gap-0">
      {steps.map((step, idx) => (
        <div key={step.num} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                step.num < currentStep
                  ? 'bg-success-500 text-white'
                  : step.num === currentStep
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-dark-800 text-dark-400 border border-dark-700'
              }`}
            >
              {step.num < currentStep ? <Check size={16} /> : step.num}
            </div>
            <span
              className={`mt-1.5 text-xs font-medium ${
                step.num === currentStep ? 'text-primary-600' : 'text-dark-400'
              }`}
            >
              {step.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`w-12 h-0.5 mx-1 mt-[-18px] ${
                step.num < currentStep ? 'bg-success-500' : 'bg-dark-700'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// --- Main Component ---
export default function SetupWizard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, setSetupCompleted, logout } = useAuthStore();

  // Language toggle function
  const toggleLanguage = () => {
    const newLang = i18n.language === 'it' ? 'en' : 'it';
    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  // Existing data (pre-populated from DB)
  const [existingProxies, setExistingProxies] = useState<Proxy[]>([]);
  const [existingServers, setExistingServers] = useState<XaccelServer[]>([]);
  const [daznConfigured, setDaznConfigured] = useState(false);

  // Step 1/3 created items
  const [createdProxy, setCreatedProxy] = useState<Proxy | null>(null);
  const [createdServer, setCreatedServer] = useState<XaccelServer | null>(null);

  // Test states
  const [proxyTesting, setProxyTesting] = useState(false);
  const [proxyTestResult, setProxyTestResult] = useState<boolean | null>(null);
  const [xaccelTesting, setXaccelTesting] = useState(false);
  const [xaccelTestResult, setXaccelTestResult] = useState<boolean | null>(null);

  // Device ID retrieval states
  const [isRetrievingDeviceId, setIsRetrievingDeviceId] = useState(false);
  const [deviceIdError, setDeviceIdError] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Completing
  const [isCompleting, setIsCompleting] = useState(false);

  // Redirect if already completed
  useEffect(() => {
    if (user?.setupCompleted) {
      navigate('/dashboard', { replace: true });
    }
  }, [user?.setupCompleted, navigate]);

  // --- Forms ---
  const daznForm = useForm<DaznStepForm>({
    resolver: zodResolver(daznStepSchema),
    defaultValues: {
      dazn_country: DAZN_DEFAULTS.COUNTRY,
      dazn_language: DAZN_DEFAULTS.LANGUAGE,
      dazn_timezone: DAZN_DEFAULTS.TIMEZONE,
      dazn_pin: '',
      // Hidden identity fields: filled after the load from the server's defaults (the Android TV user agent).
      dazn_user_agent: '',
      dazn_x_dazn_ua: '',
    },
  });

  const proxyForm = useForm<ProxyStepForm>({
    resolver: zodResolver(proxyStepSchema),
    defaultValues: {
      proxy_type: 'http',
      proxy_port: 8080,
    },
  });

  const xaccelForm = useForm<XaccelStepForm>({
    resolver: zodResolver(xaccelStepSchema),
    defaultValues: {
      server_type: 'xaccel',
    },
  });

  // Load existing data
  useEffect(() => {
    async function load() {
      try {
        const [accountRes, proxies, servers] = await Promise.all([
          daznService.getAccount().catch(() => null),
          proxyService.list().catch(() => []),
          xaccelService.list().catch(() => []),
        ]);

        if (accountRes?.account?.dazn_email) {
          setDaznConfigured(true);
          // Pre-fill DAZN form with existing values, use defaults for empty fields
          daznForm.reset({
            dazn_email: accountRes.account.dazn_email || '',
            dazn_password: '',
            dazn_device_id: accountRes.account.dazn_device_id || '',
            dazn_country: accountRes.account.dazn_country || DAZN_DEFAULTS.COUNTRY,
            dazn_language: accountRes.account.dazn_language || DAZN_DEFAULTS.LANGUAGE,
            dazn_timezone: accountRes.account.dazn_timezone || DAZN_DEFAULTS.TIMEZONE,
            dazn_pin: (accountRes.account as { dazn_pin?: string | null }).dazn_pin || '',
            // Stored values kept; only empty ones take the server's defaults (the Android TV user agent).
            ...wizardIdentityFields(accountRes.account, accountRes.identityDefaults),
          });
        } else {
          // New account: the identity defaults come from the server (one source of truth), never from a client constant
          // (a row without e-mail keeps whatever it already stores).
          const identity = wizardIdentityFields(accountRes?.account, accountRes?.identityDefaults);
          daznForm.setValue('dazn_user_agent', identity.dazn_user_agent);
          daznForm.setValue('dazn_x_dazn_ua', identity.dazn_x_dazn_ua);
        }

        setExistingProxies(proxies);
        setExistingServers(servers);
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch country to auto-select timezone and language
  const watchedCountry = daznForm.watch('dazn_country');
  useEffect(() => {
    if (watchedCountry) {
      if (COUNTRY_TIMEZONE_MAP[watchedCountry]) {
        daznForm.setValue('dazn_timezone', COUNTRY_TIMEZONE_MAP[watchedCountry]);
      }
      if (COUNTRY_LANGUAGE_MAP[watchedCountry]) {
        daznForm.setValue('dazn_language', COUNTRY_LANGUAGE_MAP[watchedCountry]);
      }
    }
  }, [watchedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Step Handlers ---
  const handleProxySubmit = async (data: ProxyStepForm) => {
    try {
      const proxy = await proxyService.create({
        name: data.name || `Proxy ${data.proxy_host}`,
        proxy_host: data.proxy_host,
        proxy_port: data.proxy_port,
        proxy_type: data.proxy_type,
        proxy_username: data.proxy_username || undefined,
        proxy_password: data.proxy_password || undefined,
        is_default: true,
      });
      setCreatedProxy(proxy);
      setExistingProxies((prev) => [...prev, proxy]);
      toast.success(t('proxy.created'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleTestProxy = async (proxyId: number) => {
    setProxyTesting(true);
    setProxyTestResult(null);
    try {
      const result = await proxyService.test(proxyId);
      setProxyTestResult(result.working);
      if (result.working) {
        toast.success(t('wizard.testSuccess'));
      } else {
        toast.error(t('wizard.testFailed'));
      }
    } catch (error) {
      setProxyTestResult(false);
      toast.error(getErrorMessage(error) || t('wizard.testFailed'));
    } finally {
      setProxyTesting(false);
    }
  };

  const handleDaznSubmit = async (data: DaznStepForm) => {
    if (!daznConfigured && !data.dazn_password) {
      daznForm.setError('dazn_password', { message: 'validation.required' });
      return;
    }
    try {
      const payload = {
        dazn_email: data.dazn_email,
        dazn_password: data.dazn_password || undefined,
        dazn_device_id: data.dazn_device_id || undefined,
        dazn_country: data.dazn_country,
        dazn_language: data.dazn_language,
        dazn_timezone: data.dazn_timezone,
        dazn_pin: data.dazn_pin || undefined,
        ...identityPayload(data),
      };
      await daznService.updateAccount(payload);
      setDaznConfigured(true);
      toast.success(t('common.saved'));
      setCurrentStep(3);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleRetrieveDeviceId = async () => {
    // Get form values
    const email = daznForm.getValues('dazn_email');
    const password = daznForm.getValues('dazn_password');
    const country = daznForm.getValues('dazn_country');

    // Validate required fields
    if (!email || !password) {
      toast.error(t('wizard.fillEmailPassword'));
      return;
    }

    // Get proxy ID (created or existing default)
    const proxy = createdProxy || existingProxies.find((p) => p.is_default) || existingProxies[0];
    if (!proxy) {
      toast.error(t('wizard.noProxyConfigured'));
      return;
    }

    setIsRetrievingDeviceId(true);
    setDeviceIdError(null);

    try {
      const result = await daznService.autoGetDeviceId({
        email,
        password,
        proxy_id: proxy.id,
        country,
        language: daznForm.getValues('dazn_language'),
      });

      // Set the device ID in the form
      daznForm.setValue('dazn_device_id', result.deviceId);
      toast.success(t('wizard.deviceIdRetrieved'));
    } catch (error: unknown) {
      // Handle specific error codes
      const errorResponse = error as { response?: { data?: { errorCode?: string; error?: string | { message?: string } } } };
      const errorCode = errorResponse?.response?.data?.errorCode;
      // Backend sends error as { message, stack } object or as string
      const rawError = errorResponse?.response?.data?.error;
      const errorMessage = typeof rawError === 'string' ? rawError : rawError?.message;

      let displayError: string;
      switch (errorCode) {
        case 'INVALID_CREDENTIALS':
          displayError = t('wizard.invalidCredentials');
          break;
        case 'ACCOUNT_BANNED':
          displayError = t('wizard.accountBanned');
          break;
        case 'TIMEOUT':
          displayError = t('wizard.deviceIdTimeout');
          break;
        case 'NETWORK_ERROR':
          displayError = t('wizard.networkError');
          break;
        default:
          displayError = errorMessage || getErrorMessage(error) || t('wizard.deviceIdError');
      }

      setDeviceIdError(displayError);
      toast.error(displayError);
    } finally {
      setIsRetrievingDeviceId(false);
    }
  };

  const handleXaccelSubmit = async (data: XaccelStepForm) => {
    try {
      const server = await xaccelService.create({
        name: data.name || `Server ${new URL(data.server_url).hostname}`,
        server_url: data.server_url,
        server_token: data.server_token,
        server_type: data.server_type || 'xaccel',
        is_default: true,
      });
      setCreatedServer(server);
      setExistingServers((prev) => [...prev, server]);
      toast.success(t('common.created'));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleTestXaccel = async (serverId: number) => {
    setXaccelTesting(true);
    setXaccelTestResult(null);
    try {
      const result = await xaccelService.test(serverId);
      setXaccelTestResult(result.working);
      if (result.working) {
        toast.success(t('wizard.testSuccess'));
      } else {
        toast.error(t('wizard.testFailed'));
      }
    } catch (error) {
      setXaccelTestResult(false);
      toast.error(getErrorMessage(error) || t('wizard.testFailed'));
    } finally {
      setXaccelTesting(false);
    }
  };

  const summaryServer = createdServer || existingServers.find((s) => s.is_default) || existingServers[0];

  const handleComplete = async () => {
    setIsCompleting(true);
    try {
      // Auto-login DAZN in background before completing setup
      try {
        await daznService.login();
        // Login successful - user will see their profile on dashboard
      } catch (loginError) {
        // Non-blocking: shown on the summary step, the user can log in later from the DAZN account page
        setLoginError(getErrorMessage(loginError) || t('wizard.loginFailed'));
        toast.error(t('wizard.loginFailed'));
      }

      // Auto-sync X Accel proxies in background (not for SSP: proxies live on the panel side)
      try {
        if ((summaryServer?.server_type || 'xaccel') !== 'ssp') await xaccelProxyService.syncProxies();
      } catch (syncError) {
        // Ignore sync errors - user can sync manually later
        console.warn('Auto-sync Xaccel proxies failed:', syncError);
      }

      await accountService.completeSetup();
      setSetupCompleted(true);
      toast.success(t('wizard.setupComplete'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsCompleting(false);
    }
  };

  // --- Advanced toggle ---
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-primary-600" />
      </div>
    );
  }

  const hasProxies = existingProxies.length > 0;
  const hasServers = existingServers.length > 0;

  // Summary data for step 4
  const summaryProxy = createdProxy || existingProxies.find((p) => p.is_default) || existingProxies[0];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-dark-700 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary-600 flex items-center justify-center">
              <Zap size={18} className="text-white" />
            </div>
            <span className="text-lg font-bold text-dark-100">DAZN Panel</span>
          </div>
          <StepIndicator currentStep={currentStep} />
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-sm text-dark-400 hover:text-dark-200 transition-colors"
              title={i18n.language === 'it' ? 'English' : 'Italiano'}
            >
              <Globe size={16} />
              {i18n.language === 'it' ? 'EN' : 'IT'}
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 text-sm text-dark-400 hover:text-dark-200 transition-colors"
            >
              <LogOut size={16} />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center pt-8 pb-12 px-4">
        <div className="w-full max-w-2xl">
          {/* Step 1: Proxy (now first!) */}
          {currentStep === 1 && (
            <div className="bg-white border border-dark-700 rounded-lg shadow-card">
              <div className="p-6 border-b border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <Shield size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-dark-100">{t('wizard.step2Title')}</h2>
                    <p className="text-dark-400 text-sm">{t('wizard.step2Desc')}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Note: Why proxy first */}
                <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={16} className="text-primary-600 mt-0.5 flex-shrink-0" />
                    <p className="text-primary-700 text-sm">{t('wizard.proxyFirstNote')}</p>
                  </div>
                </div>

                {/* Existing proxies */}
                {hasProxies && !createdProxy && (
                  <div className="bg-success-50 border border-success-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-success-600" />
                      <span className="text-success-600 font-medium text-sm">{t('wizard.alreadyConfigured')}</span>
                    </div>
                    <ul className="space-y-1">
                      {existingProxies.map((p) => (
                        <li key={p.id} className="text-dark-300 text-sm">
                          {p.name || `${p.proxy_host}:${p.proxy_port}`} ({p.proxy_type})
                          {p.is_default ? <span className="ml-1 text-primary-600 text-xs font-medium">Default</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Created proxy */}
                {createdProxy && (
                  <div className="bg-success-50 border border-success-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-success-600" />
                      <span className="text-success-600 font-medium text-sm">{t('common.created')}</span>
                    </div>
                    <p className="text-dark-300 text-sm">
                      {createdProxy.name || `${createdProxy.proxy_host}:${createdProxy.proxy_port}`} ({createdProxy.proxy_type})
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleTestProxy(createdProxy.id)}
                        disabled={proxyTesting}
                        className="btn-secondary text-sm flex items-center gap-2"
                      >
                        {proxyTesting ? <Loader2 size={14} className="animate-spin" /> : null}
                        {t('wizard.testProxy')}
                      </button>
                      {proxyTestResult === true && (
                        <span className="flex items-center gap-1 text-success-600 text-sm font-medium">
                          <Check size={14} /> {t('wizard.testSuccess')}
                        </span>
                      )}
                      {proxyTestResult === false && (
                        <span className="text-error-600 text-sm font-medium">{t('wizard.testFailed')}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Form - only show if no proxies exist and nothing created yet */}
                {!hasProxies && !createdProxy && (
                  <form onSubmit={proxyForm.handleSubmit(handleProxySubmit)} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="label">{t('common.name')}</label>
                        <input type="text" className="input" placeholder="My Proxy" {...proxyForm.register('name')} />
                      </div>
                      <div>
                        <label className="label">{t('proxy.type')}</label>
                        <select className="input" {...proxyForm.register('proxy_type')}>
                          <option value="http">HTTP</option>
                          <option value="https">HTTPS</option>
                          <option value="socks5">SOCKS5</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="label">{t('proxy.host')} *</label>
                        <input
                          type="text"
                          className={proxyForm.formState.errors.proxy_host ? 'input-error' : 'input'}
                          placeholder="proxy.example.com"
                          {...proxyForm.register('proxy_host')}
                        />
                        {proxyForm.formState.errors.proxy_host && (
                          <p className="mt-1 text-sm text-error-600">{t(proxyForm.formState.errors.proxy_host.message || '')}</p>
                        )}
                      </div>
                      <div>
                        <label className="label">{t('proxy.port')} *</label>
                        <input
                          type="number"
                          className={proxyForm.formState.errors.proxy_port ? 'input-error' : 'input'}
                          placeholder="8080"
                          {...proxyForm.register('proxy_port')}
                        />
                        {proxyForm.formState.errors.proxy_port && (
                          <p className="mt-1 text-sm text-error-600">{t('validation.invalidPort')}</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="label">{t('proxy.username')} <span className="text-dark-500 text-xs">({t('common.optional')})</span></label>
                        <input type="text" className="input" {...proxyForm.register('proxy_username')} />
                      </div>
                      <div>
                        <label className="label">{t('proxy.password')} <span className="text-dark-500 text-xs">({t('common.optional')})</span></label>
                        <input type="password" className="input" {...proxyForm.register('proxy_password')} />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={proxyForm.formState.isSubmitting}
                        className="btn-primary flex items-center gap-2"
                      >
                        {proxyForm.formState.isSubmitting ? <Loader2 size={16} className="animate-spin" /> : null}
                        {t('wizard.createAndContinue')}
                      </button>
                    </div>
                  </form>
                )}

                {/* Navigation */}
                <div className="flex justify-end pt-4 border-t border-dark-700">
                  {(hasProxies || createdProxy) && (
                    <button onClick={() => setCurrentStep(2)} className="btn-primary flex items-center gap-2">
                      {t('common.next')}
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: DAZN Account (was step 1) */}
          {currentStep === 2 && (
            <div className="bg-white border border-dark-700 rounded-lg shadow-card">
              <div className="p-6 border-b border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <User size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-dark-100">{t('wizard.step1Title')}</h2>
                    <p className="text-dark-400 text-sm">{t('wizard.step1Desc')}</p>
                  </div>
                </div>
              </div>

              <form onSubmit={daznForm.handleSubmit(handleDaznSubmit)} className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="label">{t('dazn.email')} *</label>
                    <input
                      type="email"
                      className={daznForm.formState.errors.dazn_email ? 'input-error' : 'input'}
                      placeholder="email@dazn.com"
                      {...daznForm.register('dazn_email')}
                    />
                    {daznForm.formState.errors.dazn_email && (
                      <p className="mt-1 text-sm text-error-600">{t(daznForm.formState.errors.dazn_email.message || '')}</p>
                    )}
                  </div>
                  <div>
                    <label className="label">
                      {t('dazn.password')} *
                      {daznConfigured && (
                        <span className="text-dark-500 font-normal text-xs ml-2">({t('wizard.leaveEmptyToKeep')})</span>
                      )}
                    </label>
                    <input
                      type="password"
                      className={daznForm.formState.errors.dazn_password ? 'input-error' : 'input'}
                      placeholder="Password DAZN"
                      {...daznForm.register('dazn_password')}
                    />
                    {daznForm.formState.errors.dazn_password && (
                      <p className="mt-1 text-sm text-error-600">{t(daznForm.formState.errors.dazn_password.message || '')}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="label">{t('dazn.country')} *</label>
                    <select className="input" {...daznForm.register('dazn_country')}>
                      {DAZN_COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">{t('dazn.timezone')} *</label>
                    <select className="input" {...daznForm.register('dazn_timezone')}>
                      {TIMEZONES.map((tz) => (
                        <option key={tz.code} value={tz.code}>
                          {tz.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Device ID with Retrieve button */}
                <div>
                  <label className="label">{t('dazn.deviceId')}</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input font-mono flex-1"
                      placeholder="0065f42b80"
                      {...daznForm.register('dazn_device_id')}
                    />
                    <button
                      type="button"
                      onClick={handleRetrieveDeviceId}
                      disabled={isRetrievingDeviceId}
                      className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                    >
                      {isRetrievingDeviceId ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                      {isRetrievingDeviceId ? t('wizard.retrievingDeviceId') : t('wizard.retrieveDeviceId')}
                    </button>
                  </div>
                  {deviceIdError && (
                    <p className="mt-1 text-sm text-error-600">{deviceIdError}</p>
                  )}
                  <p className="mt-1 text-xs text-dark-500">{t('wizard.deviceIdNote')}</p>
                </div>

                {/* Advanced */}
                <div className="border-t border-dark-700 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="text-dark-400 text-sm hover:text-dark-300 transition-colors"
                  >
                    {t('wizard.advanced')} {showAdvanced ? '▾' : '▸'}
                  </button>
                  {showAdvanced && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="label">{t('dazn.pin')}</label>
                        <input
                          type="text"
                          className="input"
                          maxLength={4}
                          placeholder="1234"
                          {...daznForm.register('dazn_pin')}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between pt-4 border-t border-dark-700">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <ChevronLeft size={16} />
                    {t('common.back')}
                  </button>
                  <button
                    type="submit"
                    disabled={daznForm.formState.isSubmitting}
                    className="btn-primary flex items-center gap-2"
                  >
                    {daznForm.formState.isSubmitting ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : null}
                    {t('common.next')}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Streaming Server */}
          {currentStep === 3 && (
            <div className="bg-white border border-dark-700 rounded-lg shadow-card">
              <div className="p-6 border-b border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <Server size={20} className="text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-dark-100">{t('streaming.title')}</h2>
                    <p className="text-dark-400 text-sm">{t('streaming.wizardDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* Existing servers */}
                {hasServers && !createdServer && (
                  <div className="bg-success-50 border border-success-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-success-600" />
                      <span className="text-success-600 font-medium text-sm">{t('wizard.alreadyConfigured')}</span>
                    </div>
                    <ul className="space-y-1">
                      {existingServers.map((s) => (
                        <li key={s.id} className="text-dark-300 text-sm">
                          {s.name} — {s.server_url}
                          {s.is_default ? <span className="ml-1 text-primary-600 text-xs font-medium">Default</span> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Created server */}
                {createdServer && (
                  <div className="bg-success-50 border border-success-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check size={16} className="text-success-600" />
                      <span className="text-success-600 font-medium text-sm">{t('common.created')}</span>
                    </div>
                    <p className="text-dark-300 text-sm">
                      {createdServer.name} — {createdServer.server_url}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleTestXaccel(createdServer.id)}
                        disabled={xaccelTesting}
                        className="btn-secondary text-sm flex items-center gap-2"
                      >
                        {xaccelTesting ? <Loader2 size={14} className="animate-spin" /> : null}
                        {t('wizard.testXaccel')}
                      </button>
                      {xaccelTestResult === true && (
                        <span className="flex items-center gap-1 text-success-600 text-sm font-medium">
                          <Check size={14} /> {t('wizard.testSuccess')}
                        </span>
                      )}
                      {xaccelTestResult === false && (
                        <span className="text-error-600 text-sm font-medium">{t('wizard.testFailed')}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Form */}
                {!hasServers && !createdServer && (
                  <form onSubmit={xaccelForm.handleSubmit(handleXaccelSubmit)} className="space-y-5">
                    {/* Server Type Radio */}
                    <div>
                      <label className="label">{t('streaming.serverType')}</label>
                      <Controller
                        name="server_type"
                        control={xaccelForm.control}
                        render={({ field }) => (
                          <div className="flex gap-3">
                            {CREATABLE_SERVER_TYPES.map((type) => (
                              <label
                                key={type}
                                className={`flex-1 flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  field.value === type
                                    ? 'border-primary-500 bg-primary-50'
                                    : 'border-dark-700 hover:border-dark-500'
                                }`}
                              >
                                <input
                                  type="radio"
                                  className="sr-only"
                                  checked={field.value === type}
                                  onChange={() => field.onChange(type)}
                                />
                                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                  field.value === type ? 'border-primary-600' : 'border-dark-500'
                                }`}>
                                  {field.value === type && (
                                    <div className="w-2 h-2 rounded-full bg-primary-600" />
                                  )}
                                </div>
                                <span className="font-medium text-dark-100 text-sm">
                                  {serverTypeLabel(type)}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}
                      />
                    </div>
                    <div>
                      <label className="label">{t('common.name')}</label>
                      <input type="text" className="input" placeholder={xaccelForm.watch('server_type') === 'ssp' ? 'SSP Panel' : 'Xaccel Server'} {...xaccelForm.register('name')} />
                    </div>
                    <div>
                      <label className="label">{t('xaccel.serverUrl')} *</label>
                      <input
                        type="text"
                        className={xaccelForm.formState.errors.server_url ? 'input-error' : 'input'}
                        placeholder={xaccelForm.watch('server_type') === 'ssp' ? 'https://panel.example.com (SSP)' : 'https://xaccel.example.com'}
                        {...xaccelForm.register('server_url')}
                      />
                      {xaccelForm.formState.errors.server_url && (
                        <p className="mt-1 text-sm text-error-600">{t(xaccelForm.formState.errors.server_url.message || '')}</p>
                      )}
                    </div>
                    <div>
                      <label className="label">
                        {xaccelForm.watch('server_type') === 'ssp' ? t('streaming.apiKey') : t('xaccel.serverToken')} *
                      </label>
                      <input
                        type="text"
                        className={xaccelForm.formState.errors.server_token ? 'input-error' : 'input'}
                        placeholder={xaccelForm.watch('server_type') === 'ssp' ? 'API Key (Bearer)' : t('xaccel.serverToken')}
                        {...xaccelForm.register('server_token')}
                      />
                      {xaccelForm.formState.errors.server_token && (
                        <p className="mt-1 text-sm text-error-600">{t(xaccelForm.formState.errors.server_token.message || '')}</p>
                      )}
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={xaccelForm.formState.isSubmitting}
                        className="btn-primary flex items-center gap-2"
                      >
                        {xaccelForm.formState.isSubmitting ? <Loader2 size={16} className="animate-spin" /> : null}
                        {t('wizard.createAndContinue')}
                      </button>
                    </div>
                  </form>
                )}

                {/* Navigation */}
                <div className="flex justify-between pt-4 border-t border-dark-700">
                  <button onClick={() => setCurrentStep(2)} className="btn-secondary flex items-center gap-2">
                    <ChevronLeft size={16} />
                    {t('common.back')}
                  </button>
                  {(hasServers || createdServer) && (
                    <button onClick={() => setCurrentStep(4)} className="btn-primary flex items-center gap-2">
                      {t('common.next')}
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {currentStep === 4 && (
            <div className="bg-white border border-dark-700 rounded-lg shadow-card">
              <div className="p-6 border-b border-dark-700 text-center">
                <div className="w-14 h-14 rounded-full bg-success-50 flex items-center justify-center mx-auto mb-3">
                  <Check size={28} className="text-success-600" />
                </div>
                <h2 className="text-xl font-bold text-dark-100">{t('wizard.summaryTitle')}</h2>
                <p className="text-dark-400 text-sm mt-1">{t('wizard.summaryDesc')}</p>
                {loginError && <p className="mt-2 text-sm text-error-600">{t('wizard.loginFailed')}: {loginError}</p>}
                {!daznForm.getValues('dazn_device_id') && <p className="mt-2 text-sm text-warning-600">{t('wizard.deviceIdMissing')}</p>}
                <div className="mt-4 p-3 rounded-lg border border-warning-200 bg-warning-50 text-warning-700 text-sm font-medium">{t('wizard.wvdWarning')}</div>
                <div className="mt-3"><WvdCard /></div>
              </div>

              <div className="p-6 space-y-4">
                {/* Proxy (now first in summary too) */}
                <div className="flex items-center gap-4 p-4 rounded-lg border border-dark-700 bg-dark-800/30">
                  <div className="w-10 h-10 rounded-lg bg-success-50 flex items-center justify-center flex-shrink-0">
                    <Check size={18} className="text-success-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-dark-100">Proxy</p>
                    <p className="text-dark-400 text-sm truncate">
                      {summaryProxy
                        ? `${summaryProxy.name || summaryProxy.proxy_host + ':' + summaryProxy.proxy_port} (${summaryProxy.proxy_type})`
                        : '-'}
                    </p>
                  </div>
                </div>

                {/* DAZN Account */}
                <div className="flex items-center gap-4 p-4 rounded-lg border border-dark-700 bg-dark-800/30">
                  <div className="w-10 h-10 rounded-lg bg-success-50 flex items-center justify-center flex-shrink-0">
                    <Check size={18} className="text-success-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-dark-100">Account DAZN</p>
                    <p className="text-dark-400 text-sm truncate">
                      {daznForm.getValues('dazn_email')} — {DAZN_COUNTRIES.find((c) => c.code === daznForm.getValues('dazn_country'))?.name || daznForm.getValues('dazn_country')}
                    </p>
                  </div>
                </div>

                {/* Streaming Server */}
                <div className="flex items-center gap-4 p-4 rounded-lg border border-dark-700 bg-dark-800/30">
                  <div className="w-10 h-10 rounded-lg bg-success-50 flex items-center justify-center flex-shrink-0">
                    <Check size={18} className="text-success-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-dark-100">{t('streaming.title')}</p>
                    <p className="text-dark-400 text-sm truncate">
                      {summaryServer ? `${summaryServer.name} — ${summaryServer.server_url} [${serverTypeLabel(summaryServer.server_type)}]` : '-'}
                    </p>
                  </div>
                </div>

                {/* Complete Button */}
                <div className="flex justify-between pt-4 border-t border-dark-700">
                  <button onClick={() => setCurrentStep(3)} className="btn-secondary flex items-center gap-2">
                    <ChevronLeft size={16} />
                    {t('common.back')}
                  </button>
                  <button
                    onClick={handleComplete}
                    disabled={isCompleting}
                    className="btn-primary px-6 py-2.5 flex items-center gap-2"
                  >
                    {isCompleting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                    {t('wizard.completeSetup')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
